import type { FormEvent } from 'react'
import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth, roleHome } from '@/context/AuthContext'
import AuthShell from './AuthShell'
import { api, errorMessage, fieldErrors } from '@/lib/api'
import Button from '@/components/ui/Button'
import { Field, Input, PasswordInput } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'

export default function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const toast = useToast()
  const [form, setForm] = useState({ email: '', password: '' })
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [loading, setLoading] = useState(false)
  const [params] = useSearchParams()

  // The button only appears once an admin has switched Google on under
  // admin > Integrations, so a half-configured provider is never offered.
  const providers = useQuery({
    queryKey: ['auth-providers'],
    queryFn: async () => (await api.get<{ google: boolean }>('/auth/providers')).data,
    staleTime: 5 * 60 * 1000,
  })

  // The OAuth callback redirects back here with a reason when it cannot
  // complete, since it has no way to render a message itself.
  useEffect(() => {
    const oauth = params.get('oauth')
    if (oauth === 'failed') toast.error('Google sign in failed', 'Please try again or use your password.')
    if (oauth === 'suspended') toast.error('Account suspended', 'Please contact MPG Printing.')
    if (oauth === 'unverified') toast.error('Google address not verified', 'Google has not verified that email address. Sign in with your password instead.')
    if (oauth === 'not_customer') toast.error('Team accounts sign in with a password', 'Google sign in is for customer accounts only.')
  }, [params, toast])

  const from = (location.state as { from?: string } | null)?.from

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setErrors({})
    try {
      const user = await login(form)
      navigate(from ?? roleHome(user))
    } catch (err) {
      setErrors(fieldErrors(err))
      toast.error('Sign in failed', errorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell
      heading="Sign In"
      subheading="A few more clicks to sign in to your account"
      tagline={
        <>
          A few more clicks to <br /> sign in to your account.
        </>
      }
    >
      <form onSubmit={submit} className="mt-7 space-y-4">
        <Field error={errors.email?.[0]} htmlFor="email">
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            required
          />
        </Field>
        <Field error={errors.password?.[0]} htmlFor="password">
          <PasswordInput
            id="password"
            autoComplete="current-password"
            placeholder="Password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            required
          />
        </Field>

        <div className="grid grid-cols-2 gap-3 pt-2">
          <Button type="submit" loading={loading}>
            Login
          </Button>
          <Link to="/register">
            <Button variant="outline" className="w-full">
              Register
            </Button>
          </Link>
        </div>
        <p className="pt-1 text-center text-xs text-foreground/60">
          <Link to="/forgot-password" className="font-medium hover:text-primary">
            Forgot your password?
          </Link>
        </p>
      </form>

      {providers.data?.google && (
        <Button variant="outline" className="mt-3 w-full" onClick={() => {
          // A full page navigation, not an XHR: the browser has to follow
          // Google's redirects and come back with the consent result.
          window.location.href = '/api/v1/auth/google/redirect'
        }}>
          <svg className="size-4" viewBox="0 0 24 24" aria-hidden="true">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z" />
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
            <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15A11 11 0 0 0 2.18 7.06L5.84 9.9C6.71 7.31 9.14 5.38 12 5.38Z" />
          </svg>
          Continue with Google
        </Button>
      )}
    </AuthShell>
  )
}
